import styles from "../chat-bubble.module.css";
import Image from "next/image";
import {
  AssistantPayload,
  AssistantProfile,
  MessageListingCard,
  StoredChat,
} from "../../../../../../../../lib/chat-store/types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useEffect, useRef } from "react";
import ListingCard from "../listing-card";
import {
  IoCalendarClearOutline,
  IoRefreshOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { UserTypes } from "@/types/types";
import { useTranslations } from "next-intl";
import { useChatStore } from "../../../../../../../../lib/chat-store/chats-store";

export default function AssistantBubble({
  payload,
  timestamp,
  sender_id,
  chatData,
}: {
  payload: AssistantPayload;
  timestamp: number;
  sender_id: string; // 🚀 NEW
  chatData: StoredChat | undefined;
}) {
  const { ui_type, data } = payload;
  const mapUrl = data?.map_url as string | undefined;
  const swiperHintRef = useRef<HTMLDivElement | null>(null);
  const { userData } = useUserAuthDataContext();
  const t = useTranslations("ChatsView.assistant");
  const assistantSub = sender_id?.startsWith("assistant-")
    ? sender_id.replace("assistant-", "")
    : null;
  const activeAssistant = assistantSub
    ? chatData?.assistants?.[assistantSub]
    : null;
  const personalityId = activeAssistant?.personality || "professional";
  const { sendMessage } = useChatStore();
  let displayText = "";
  if (data?.text) {
    // Generative types (GPT) -> Render raw text
    if (ui_type === "text" || ui_type === "listing") {
      displayText = data.text;
    }
    // Deterministic types -> Render translation
    // e.g. t('system.agent_notified') or t('listing_search.listings-returned')
    else {
      displayText = t(`${ui_type}.${data.text}.${personalityId}`);
    }
  }

  // === HELPERS ===
  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSlotTime = (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTourDate = (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const onAction = (actionType: string, actionData: any, index?: number) => {
    if (!chatData) return;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let messageText = "";

    switch (actionType) {
      case "pick_slot":
        const isMidnight =
          actionData.includes("T00:00:00") || actionData.endsWith("00:00:00");

        if (isMidnight) {
          // User picked a DAY
          // Key: "{date} works for me"
          messageText = t("user_reply.date_works", {
            date: formatTourDate(actionData),
          });
        } else {
          // User picked a TIME
          // Key: "{time} works for me"
          messageText = t("user_reply.time_works", {
            time: formatSlotTime(actionData),
          });
        }
        break;
      case "show_more":
        if (data?.text === "day") {
          messageText = t("user_reply.show_other_days");
        } else {
          messageText = t("user_reply.show_other_times");
        }
        break;

      case "select_listing":
        const title = actionData.title || "this property";
        messageText = t("user_reply.interested_in", { title });
        break;

      case "select_tour":
        const tourNumber = (index ?? 0) + 1;
        messageText = t("user_reply.modify_tour", { number: tourNumber });
        break;
    }

    if (messageText) {
      sendMessage(
        chatData.conversation_id,
        "text",
        { text: messageText, timezone: timezone },
        chatData.receiver_id,
      );
    }
  };

  // === SWIPER ANIMATION ===
  const listings = (data?.listings as MessageListingCard[]) || [];
  const showListings =
    !mapUrl &&
    (ui_type === "listing" ||
      ui_type === "listing_selection" ||
      ui_type === "listing_search") && // Added listing-search support
    listings.length > 0;

  useEffect(() => {
    if (!swiperHintRef.current || !showListings) return;

    const el = swiperHintRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            const wrapper = swiperHintRef.current?.querySelector(
              ".swiper-wrapper",
            ) as HTMLElement;

            if (!wrapper) return;
            wrapper.style.willChange = "transform";
            const runHint = () => {
              wrapper.animate(
                [
                  { transform: "translateX(0)" },
                  { transform: "translateX(-28px)" },
                  { transform: "translateX(0)" },
                ],
                {
                  duration: 550,
                  easing: "cubic-bezier(.4,0,.2,1)",
                },
              );
            };

            runHint();
            setTimeout(() => {
              runHint();
              setTimeout(() => {
                wrapper.style.willChange = "auto";
              }, 560);
            }, 700);
          }, 500);
          observer.disconnect();
        }
      },
      { threshold: 0.7 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [showListings]);

  // === RENDERERS ===

  // 1. System / Error / Info Messages
  // These usually appear centered or distinct from the chat bubble
  if (ui_type === "error" || ui_type === "info") {
    return (
      <div className={styles.systemRow}>
        <div
          className={`${styles.systemMessage} ${
            ui_type === "error" ? styles.error : ""
          }`}
        >
          {displayText || "System notification"}
        </div>
      </div>
    );
  }
  const assistantName = activeAssistant?.name
    ? `${activeAssistant.name} ${t("aiAssistant")}`
    : t("aiAssistant");
  const assistantAvatar = activeAssistant?.profile_photo
    ? `/ai-avatar${activeAssistant.profile_photo}.png`
    : "/ai-avatar4.png";

  const isMyAssistant = userData.sub === assistantSub;

  return (
    <div
      className={`${styles.row} ${
        isMyAssistant ? styles.mine : styles.theirs
      } ${styles.assistantRow} ${styles.listingBubble} ${
        showListings ? styles.hasListings : ""
      } ${mapUrl ? styles.hasMap : ""}`}
    >
      <div className={styles.bubbleWrapper}>
        <div className={styles.infoAndTextWrapper}>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{assistantName}</span>
            <div className={styles.assistantAvatar}>
              <Image src={assistantAvatar} alt="AI" width={24} height={24} />
            </div>
          </span>

          {/* === A. LISTINGS CAROUSEL === */}
          {showListings && (
            <div ref={swiperHintRef}>
              <Swiper slidesPerView="auto" spaceBetween={16}>
                {listings.map((listing, index) => (
                  <SwiperSlide
                    key={listing.listing_id + index}
                    className={styles.listingSlide}
                  >
                    <ListingCard
                      listing={listing}
                      total={listings.length}
                      index={index + 1}
                    />
                    {ui_type === "listing_selection" && (
                      <button
                        className={styles.selectListingButton}
                        onClick={() => onAction("select_listing", listing)}
                      >
                        {t("selectProperty")}
                      </button>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          <div className={styles.assistantContent}>
            {/* === B. MAIN TEXT CONTENT === */}
            {displayText && <span className={styles.text}>{displayText}</span>}

            {/* === C. SLOTS SELECTION === */}
            {ui_type === "pick_slot" && data?.slots && (
              <div className={styles.slotsGrid}>
                {data.slots.map((slot: string, i: number) => {
                  // Heuristic: If it ends in 00:00:00 or T00:00:00, it's likely a Day, otherwise a Time Slot
                  const isMidnight =
                    slot.includes("T00:00:00") || slot.endsWith("00:00:00");
                  const label = isMidnight
                    ? formatTourDate(slot)
                    : formatSlotTime(slot);

                  return (
                    <button
                      key={i}
                      className={styles.slotChip}
                      onClick={() => onAction("pick_slot", slot)}
                    >
                      {isMidnight ? (
                        <IoCalendarClearOutline />
                      ) : (
                        <IoTimeOutline />
                      )}
                      {label}
                    </button>
                  );
                })}
                {data?.has_more && (
                  <button
                    className={`${styles.slotChip} ${styles.showMore}`}
                    onClick={() => onAction("show_more", null)}
                  >
                    <IoRefreshOutline />
                    {t("buttons.show_more")}
                  </button>
                )}
              </div>
            )}

            {/* === D. TOUR LIST / SELECTION === */}
            {(ui_type === "tours_list" || ui_type === "tours_selection") &&
              data?.events && (
                <div className={styles.toursList}>
                  {data.events.map((event: any, index: number) => {
                    // FIX: Handle PascalCase (DB) vs snake_case (standard API)
                    const evtId = event.event_id || event.EventId;
                    const title =
                      event.listing_title || event.ListingTitle || "Tour";
                    const slotUtc = event.slot_utc || event.SlotUtc;
                    const status = event.status || event.Status || "Scheduled";

                    return (
                      <div
                        key={evtId}
                        className={styles.tourItem}
                        onClick={() => {
                          if (ui_type === "tours_selection") {
                            onAction("select_tour", event, index);
                          }
                        }}
                      >
                        <div className={styles.tourTitle}>{title}</div>
                        <div className={styles.tourDate}>
                          <IoCalendarClearOutline />
                          {/* Ensure slotUtc exists before formatting */}
                          {slotUtc ? formatTourDate(slotUtc) : "N/A"}
                          {" • "}
                          {slotUtc ? formatSlotTime(slotUtc) : "N/A"}
                        </div>
                        <div className={styles.tourStatus}>{status}</div>
                      </div>
                    );
                  })}
                </div>
              )}

            {/* === E. CONFIRMATION / CANCELLED / RESCHEDULED CARDS === */}
            {(ui_type === "tour_confirmation" ||
              ui_type === "tour_cancelled" ||
              ui_type === "tour_rescheduled") &&
              data?.event &&
              (() => {
                // FIX: Handle PascalCase fallback here as well
                const evt = data.event;
                const title = evt.listing_title || evt.ListingTitle || "Tour";
                const slotUtc = evt.slot_utc || evt.SlotUtc;

                return (
                  <div
                    className={`${styles.eventCard} ${
                      ui_type === "tour_cancelled" ? styles.cancelled : ""
                    }`}
                  >
                    <div className={styles.tourTitle}>{title}</div>
                    <div className={styles.tourDate}>
                      <IoTimeOutline
                        style={{
                          display: "inline",
                          marginBottom: -2,
                          marginRight: 4,
                        }}
                      />
                      {slotUtc ? formatTourDate(slotUtc) : "N/A"} {t("at")}{" "}
                      {slotUtc ? formatSlotTime(slotUtc) : "N/A"}
                    </div>
                    <div className={styles.tourStatus}>
                      {ui_type === "tour_cancelled"
                        ? t("cancelled")
                        : ui_type === "tour_rescheduled"
                          ? t("reschedulted")
                          : t("confirmed")}
                    </div>
                  </div>
                );
              })()}
            {/* === F. MAP IFRAME (Rendered after text) === */}
            {mapUrl && (
              <div className={styles.mapContainer}>
                <iframe
                  src={mapUrl}
                  className={styles.mapIframe}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  href={mapUrl.replace("&output=embed", "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.mapLink}
                >
                  {t("openInGoogleMaps")}
                </a>
              </div>
            )}
          </div>
        </div>

        <div className={styles.messageInfo}>
          <span className={styles.time}>{formatTime(timestamp)}</span>
        </div>
      </div>
    </div>
  );
}
